import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { getFloat } from "@/utils/mappings";

import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "diameterEncompassingEntireThinDefectSection";
const diameterEncompassingEntireThinDefectDetailsSchema = z.object({
  pinhole_2: z.union([
    z.number().gte(0.1).lt(maxInputNumber),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
});
type DiameterEncompassingEntireThinDefectDetailsSchema = z.infer<
  typeof diameterEncompassingEntireThinDefectDetailsSchema
>;
export function DiameterEncompassingEntireThinDefectDetails() {
  const { sections, toggleExpandableSection, setDiameterEncompassingEntireThinDefectDetails } =
    useCUCompositeJobStore();
  const savedData = sections.diameterEncompassingEntireThinDefectSection?.data;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<DiameterEncompassingEntireThinDefectDetailsSchema>({
    resolver: zodResolver(diameterEncompassingEntireThinDefectDetailsSchema),
    values: {
      pinhole_2: savedData?.pinhole_2 ?? "",
    },
  });

  return (
    <ExpandableSection
      status={sections.diameterEncompassingEntireThinDefectSection?.status ?? "hidden"}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Diameter Encompassing Entire Thin Defect Details"
    >
      <div>
        <form
          className="flex flex-col items-center gap-6 md:flex-row md:gap-20"
          onSubmit={handleSubmit((data) => {
            if (!data.pinhole_2) return;
            const pinhole_2 = typeof data.pinhole_2 === "string" ? Number.parseFloat(data.pinhole_2) : data.pinhole_2;
            setDiameterEncompassingEntireThinDefectDetails({ data: { pinhole_2 }, status: "done" });
          })}
        >
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="Diameter Encompassing Entire Thin Defect Details"
            type="number"
            step={inputStep}
            error={errors.pinhole_2?.message}
            {...register("pinhole_2", { setValueAs: getFloat })}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                Inches
              </Typography>
            }
          />
          <Button className="w-full self-end md:w-fit" type="submit">
            Save
          </Button>
        </form>
      </div>
    </ExpandableSection>
  );
}
